import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/")({
  component: InventoryDashboardPage,
});

import { lazy, Suspense, useEffect, useState } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  Package,
  Warehouse,
  CheckCircle2,
  Wrench,
  Truck,
  AlarmClock,
  Undo2,
  ClipboardCheck,
  Siren,
  FileText,
  Receipt,
  ShoppingCart,
  ClipboardList,
  LayoutDashboard,
  ShieldCheck,
} from "lucide-react";
import {
  useFetchItemSafetySummary,
  useFetchLowStock,
  useFetchReorderNeeded,
} from "@/hooks/api/inventory/useCatalog";
import {
  useFetchTransactions,
  useFetchPurchaseOrders,
} from "@/hooks/api/inventory/useStockOps";
import { useFetchSalesOrders } from "@/hooks/api/inventory/useSales";
import { useInventoryReports } from "@/hooks/api/inventory/useReports";
import {
  useFetchInvoices,
  useFetchInvoiceSummary,
  type DocumentTotalsSummary,
} from "@/hooks/api/inventory/useInvoices";
import {
  useFetchBills,
  useFetchBillSummary,
} from "@/hooks/api/inventory/useBills";
import { useFetchAssetItems } from "@/hooks/api/inventory/useAssetItems";
import { useFetchRentalContracts } from "@/hooks/api/inventory/useRentalContracts";
import { useFetchAssetIncidents } from "@/hooks/api/inventory/useAssetIncidents";
import { TransactionTypeBadge } from "@/components/inventory/shared/StatusBadge";
import { SafetyMetricsCard } from "@/components/inventory/SafetyMetricsCard";
import { StockAlertsCard } from "@/components/inventory/StockAlertsCard";
import { formatToOrgDate, dayjs } from "@/lib/date-utils";
import { KpiCard } from "@/components/reusable-components/kpi-card";
/** The chart cards carry recharts with them, so they load alongside the data
 * they visualise rather than blocking the rest of the dashboard. */
const DonutChartCard = lazy(() =>
  import("@/components/reusable-components/donut-chart-card").then((m) => ({
    default: m.DonutChartCard,
  })),
);
const BarChartCard = lazy(() =>
  import("@/components/reusable-components/bar-chart-card").then((m) => ({
    default: m.BarChartCard,
  })),
);
const LineChartCard = lazy(() =>
  import("@/components/reusable-components/line-chart-card").then((m) => ({
    default: m.LineChartCard,
  })),
);
import {
  FinancialSummaryCard,
  type CurrencyTotals,
} from "@/components/inventory/FinancialSummaryCard";
import type { MovementReport, StockSummaryItem } from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

// ─── Transaction type config ──────────────────────────────────────────────────

interface TxConfig {
  label: string;
  color: string;
}

// Keyed by server-sent transaction type strings, so unknown keys fall back.
const TX_CONFIG = new Map<string, TxConfig>([
  ["PURCHASE_IN", { label: "Purchase In", color: "#10b981" }],
  ["RETURN_IN", { label: "Return In", color: "#0ea5e9" }],
  ["TRANSFER_IN", { label: "Transfer In", color: "#8b5cf6" }],
  ["ADJUSTMENT_IN", { label: "Adjust. In", color: "#6366f1" }],
  ["RENTAL_RETURN_IN", { label: "Rental Return", color: "#06b6d4" }],
  ["REQUISITION_OUT", { label: "Issue Out", color: "#ef4444" }],
  ["TRANSFER_OUT", { label: "Transfer Out", color: "#f97316" }],
  ["DAMAGE_OUT", { label: "Damage Out", color: "#f59e0b" }],
  ["ADJUSTMENT_OUT", { label: "Adjust. Out", color: "#ec4899" }],
  ["DEPLOYMENT_OUT", { label: "Deploy Out", color: "#7c3aed" }],
  ["RENTAL_OUT", { label: "Rental Out", color: "#dc2626" }],
  ["INSPECTION_OUT", { label: "Inspection", color: "#0369a1" }],
]);

function txLabel(type: string): string {
  return (
    TX_CONFIG.get(type)?.label ??
    type
      .split("_")
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(" ")
  );
}
function txColor(type: string): string {
  return TX_CONFIG.get(type)?.color ?? "#94a3b8";
}

const WAREHOUSE_COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
];

// ─── Number formatting ────────────────────────────────────────────────────────

function formatAmount(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// ─── Data helpers ─────────────────────────────────────────────────────────────

function buildWarehouseData(summary: StockSummaryItem[]) {
  const map = new Map<number, { name: string; total: number }>();
  for (const item of summary) {
    const qty = parseFloat(item.quantity) || 0;
    const existing = map.get(item.warehouse_id);
    if (existing) {
      existing.total += qty;
    } else {
      map.set(item.warehouse_id, { name: item.warehouse_name, total: qty });
    }
  }
  return Array.from(map.values())
    .sort((a, b) => b.total - a.total)
    .map((w, i) => ({
      name: w.name,
      value: Math.round(w.total * 100) / 100,
      color: WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length],
    }));
}

function buildTxTypeData(report: MovementReport | null) {
  if (!report) return [];
  return Object.entries(report.transactions_by_type)
    .map(([type, entry]) => ({
      name: txLabel(type),
      value: entry.count,
      color: txColor(type),
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
}

/** Sales/purchases line colours, one pair per currency (T-248). */
const CURRENCY_SERIES_COLORS = [
  { sales: "#10b981", purchases: "#8b5cf6" },
  { sales: "#0ea5e9", purchases: "#f59e0b" },
  { sales: "#14b8a6", purchases: "#ec4899" },
  { sales: "#84cc16", purchases: "#6366f1" },
];

/** Every currency either summary mentions, sorted. */
function summaryCurrencies(
  invoices: DocumentTotalsSummary | undefined,
  bills: DocumentTotalsSummary | undefined
): string[] {
  const set = new Set<string>();
  for (const row of invoices?.by_currency ?? []) set.add(row.currency);
  for (const row of bills?.by_currency ?? []) set.add(row.currency);
  return Array.from(set).sort();
}

/** Builds a daily sales vs purchases series from the server's per-day
 * invoice and bill totals -- one sales and one purchases line per currency,
 * since amounts in different currencies can't share a line (T-248). */
function buildPurchaseSalesData(
  invoiceDaily: DocumentTotalsSummary["daily"],
  billDaily: DocumentTotalsSummary["daily"],
  currencies: string[],
  fromDate: string,
  toDate: string
) {
  const dateMap = new Map<string, Record<string, string | number>>();

  let cursor = dayjs(fromDate);
  const end = dayjs(toDate);
  while (!cursor.isAfter(end)) {
    const entry: Record<string, string | number> = {
      label: cursor.format("MMM D"),
    };
    for (const currency of currencies) {
      entry[`sales_${currency}`] = 0;
      entry[`purchases_${currency}`] = 0;
    }
    dateMap.set(cursor.format("YYYY-MM-DD"), entry);
    cursor = cursor.add(1, "day");
  }

  const add = (
    rows: DocumentTotalsSummary["daily"],
    prefix: "sales" | "purchases"
  ) => {
    for (const day of rows) {
      const entry = dateMap.get(day.date);
      const key = `${prefix}_${day.currency}`;
      if (entry && key in entry) {
        entry[key] = (entry[key] as number) + (parseFloat(day.total) || 0);
      }
    }
  };
  add(invoiceDaily, "sales");
  add(billDaily, "purchases");

  return Array.from(dateMap.values());
}

function purchaseSalesSeriesFor(currencies: string[]) {
  const suffix = (currency: string) =>
    currencies.length > 1 ? ` (${currency})` : "";
  return currencies.flatMap((currency, i) => {
    const colors = CURRENCY_SERIES_COLORS[i % CURRENCY_SERIES_COLORS.length];
    return [
      {
        dataKey: `sales_${currency}`,
        color: colors.sales,
        label: `Sales${suffix(currency)}`,
      },
      {
        dataKey: `purchases_${currency}`,
        color: colors.purchases,
        label: `Purchases${suffix(currency)}`,
      },
    ];
  });
}

/** Revenue and purchases per currency, never summed across currencies. */
function currencyTotalsFor(
  invoices: DocumentTotalsSummary | undefined,
  bills: DocumentTotalsSummary | undefined,
  currencies: string[]
): CurrencyTotals[] {
  const totalOf = (
    summary: DocumentTotalsSummary | undefined,
    currency: string
  ) =>
    parseFloat(
      summary?.by_currency.find((row) => row.currency === currency)?.total ??
        "0"
    ) || 0;
  return currencies.map((currency) => ({
    currency,
    revenue: totalOf(invoices, currency),
    purchases: totalOf(bills, currency),
  }));
}

// ─── Tab: Overview ─────────────────────────────────────────────────────────────
// KPI cards, activity chart + warehouse chart side by side, low-stock list
// below. This is the initial active tab, so it's the only one that fetches
// on first paint.

function OverviewSection() {
  const { fetchStockSummary, fetchMovementReport } = useInventoryReports();
  const summary: StockSummaryItem[] = fetchStockSummary.data ?? [];
  const movementReport: MovementReport | null =
    fetchMovementReport.data ?? null;

  // Counts only -- the alert card pages and searches its own rows.
  const lowStockQuery = useFetchLowStock({ page_size: 1 });
  const lowStockCount = lowStockQuery.data?.count ?? 0;

  const reorderNeededQuery = useFetchReorderNeeded({ page_size: 1 });
  const reorderNeededCount = reorderNeededQuery.data?.count ?? 0;

  const transactionsQuery = useFetchTransactions({
    ordering: "-created_at",
    page_size: 10,
  });
  const transactions = transactionsQuery.data?.results ?? [];

  const today = dayjs().format("YYYY-MM-DD");
  const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");

  useEffect(() => {
    fetchStockSummary.mutate({});
    fetchMovementReport.mutate({ from_date: thirtyDaysAgo, to_date: today });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading =
    fetchStockSummary.isPending ||
    fetchMovementReport.isPending ||
    lowStockQuery.isPending ||
    reorderNeededQuery.isPending;
  const isLoadingTransactions = transactionsQuery.isPending;

  const totalLines = summary.length;
  const warehouseCount = new Set(summary.map((s) => s.warehouse_id)).size;
  const healthyCount = summary.filter((s) => !s.is_low_stock).length;
  const warehouseData = buildWarehouseData(summary);
  const txTypeData = buildTxTypeData(movementReport);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Link to="/admin/inventory/items" className="block no-underline">
          <KpiCard
            label="Stock(Item) Lines"
            value={isLoading ? "—" : totalLines.toLocaleString()}
            description="Tracked across all warehouses"
            icon={Package}
            colorScheme="indigo"
            loading={isLoading}
          />
        </Link>
        <Link to="/admin/inventory/warehouses" className="block no-underline">
          <KpiCard
            label="Active Warehouses"
            value={isLoading ? "—" : warehouseCount.toLocaleString()}
            description="Storage locations in use"
            icon={Warehouse}
            colorScheme="sky"
            loading={isLoading}
          />
        </Link>
        <Link to="/admin/inventory/items" className="block no-underline">
          <KpiCard
            label="Healthy Stock(Items)"
            value={isLoading ? "—" : healthyCount.toLocaleString()}
            description="Lines above minimum level"
            icon={CheckCircle2}
            colorScheme="emerald"
            loading={isLoading}
          />
        </Link>
        <Link to="/admin/inventory/items" className="block no-underline">
          <KpiCard
            label="Low Stock Alerts"
            value={isLoading ? "—" : lowStockCount.toLocaleString()}
            description="Items below minimum level"
            icon={AlertTriangle}
            iconClassName={
              lowStockCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              lowStockCount > 0 ? "text-red-600 dark:text-red-400" : undefined
            }
            loading={isLoading}
            className={
              lowStockCount > 0
                ? "bg-linear-to-br from-red-50/60 to-card border-red-200 dark:from-red-950/30 dark:to-card dark:border-red-900/50"
                : undefined
            }
          />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Suspense
          fallback={<Skeleton className="lg:col-span-3 h-[380px] rounded-xl" />}
        >
          <BarChartCard
            className="lg:col-span-3"
            title="Activity by Type - Last 30 days"
            description="Transaction count broken down by movement type"
            data={txTypeData}
            layout="horizontal"
            emptyText="No transactions in the last 30 days"
            loading={isLoading}
            yAxisWidth={110}
            chartHeight={280}
          />
        </Suspense>
        <Suspense
          fallback={<Skeleton className="lg:col-span-2 h-[380px] rounded-xl" />}
        >
          <DonutChartCard
            className="lg:col-span-2"
            title="Stock by Warehouse"
            description="Total quantity(available) per warehouse"
            data={warehouseData}
            centerLabel="Total qty"
            emptyText="No warehouse stock data"
            loading={isLoading}
          />
        </Suspense>
      </div>

      <StockAlertsCard
        lowStockCount={lowStockCount}
        reorderNeededCount={reorderNeededCount}
      />

      <Card className="gap-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">
            Recent Transactions
          </CardTitle>
          <CardDescription className="text-xs">
            Latest 10 stock movements across all warehouses
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {transactions.length === 0 && !isLoadingTransactions ? (
            <p className="text-center text-muted-foreground py-10 text-sm">
              No transactions yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="pl-6 text-xs">Item</TableHead>
                  <TableHead className="text-xs">Location</TableHead>
                  <TableHead className="text-xs hidden md:table-cell">
                    Date
                  </TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">
                    Remarks
                  </TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                  <TableHead className="text-xs text-right pr-6">Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.slice(0, 10).map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="pl-6">
                      <Link
                        to="/admin/inventory/items/$id"
                        params={{ id: String(tx.item) }}
                        className="font-medium text-sm hover:text-primary hover:underline transition-colors"
                      >
                        {tx.item_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {tx.warehouse_name}
                      </span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {formatToOrgDate(tx.transaction_date, "MMM D, YYYY")}
                      </span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell max-w-[200px]">
                      <span className="text-xs text-muted-foreground truncate block">
                        {tx.remarks || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TransactionTypeBadge
                        txType={tx.transaction_type}
                        display={tx.transaction_type_display}
                      />
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <span className="text-xs font-mono font-semibold tabular-nums">
                        {parseFloat(tx.quantity).toFixed(2)}
                      </span>
                      <span className="text-xs text-muted-foreground ml-1">
                        {tx.unit_abbr}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Tab: Purchases & Sales ─────────────────────────────────────────────────────

function PurchasesSalesSection() {
  // Independent report mutation instance from the Overview tab's — the two
  // tabs mount separately, so this fires its own request only when this tab
  // is opened rather than sharing Overview's fetch.
  const { fetchMovementReport } = useInventoryReports();
  const movementReport: MovementReport | null =
    fetchMovementReport.data ?? null;

  const today = dayjs().format("YYYY-MM-DD");
  const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");

  // Every figure below is counted/summed on the server over all matching
  // rows -- list pages are capped at 100, so totals derived from one page
  // silently stop growing.
  // Bounded on both sides: a future-dated document is not 30-day activity.
  const invoiceSummaryQuery = useFetchInvoiceSummary({
    invoice_date__gte: thirtyDaysAgo,
    invoice_date__lte: today,
  });
  const billSummaryQuery = useFetchBillSummary({
    bill_date__gte: thirtyDaysAgo,
    bill_date__lte: today,
  });
  const overdueInvoicesQuery = useFetchInvoices({
    status: "OVERDUE",
    page_size: 1,
  });
  const overdueBillsQuery = useFetchBills({ status: "OVERDUE", page_size: 1 });

  useEffect(() => {
    fetchMovementReport.mutate({ from_date: thirtyDaysAgo, to_date: today });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading =
    fetchMovementReport.isPending ||
    invoiceSummaryQuery.isPending ||
    billSummaryQuery.isPending ||
    overdueInvoicesQuery.isPending ||
    overdueBillsQuery.isPending;

  const currencies = summaryCurrencies(
    invoiceSummaryQuery.data,
    billSummaryQuery.data
  );
  const purchaseSalesData = buildPurchaseSalesData(
    invoiceSummaryQuery.data?.daily ?? [],
    billSummaryQuery.data?.daily ?? [],
    currencies,
    thirtyDaysAgo,
    today
  );
  const purchaseSalesSeries = purchaseSalesSeriesFor(currencies);

  // Monetary totals for the last 30 days, per currency. The server leaves
  // out DRAFT and VOID documents (T-248).
  const recentInvoicesCount = invoiceSummaryQuery.data?.count ?? 0;
  const recentBillsCount = billSummaryQuery.data?.count ?? 0;
  const currencyTotals = currencyTotalsFor(
    invoiceSummaryQuery.data,
    billSummaryQuery.data,
    currencies
  );
  const netChange = movementReport?.net_change ?? 0;

  const overdueInvoicesCount = overdueInvoicesQuery.data?.count ?? 0;
  const overdueBillsCount = overdueBillsQuery.data?.count ?? 0;

  // Sales/purchase order counts — every query here is page_size: 1 and
  // reads only the paginated `count`, with the date/status filter applied
  // on the server.
  const salesOrdersQuery = useFetchSalesOrders({
    order_date__gte: thirtyDaysAgo,
    page_size: 1,
  });
  const salesOrders30dCount = salesOrdersQuery.data?.count ?? 0;

  const purchaseOrdersQuery = useFetchPurchaseOrders({
    order_date__gte: thirtyDaysAgo,
    page_size: 1,
  });
  const purchaseOrders30dCount = purchaseOrdersQuery.data?.count ?? 0;

  const openSalesOrdersQuery = useFetchSalesOrders({
    status: "CONFIRMED",
    page_size: 1,
  });
  const openSalesOrdersCount = openSalesOrdersQuery.data?.count ?? 0;

  const openPurchaseOrdersQuery = useFetchPurchaseOrders({
    status: "ORDERED",
    page_size: 1,
  });
  const openPurchaseOrdersCount = openPurchaseOrdersQuery.data?.count ?? 0;

  const isLoadingOrders =
    salesOrdersQuery.isPending ||
    purchaseOrdersQuery.isPending ||
    openSalesOrdersQuery.isPending ||
    openPurchaseOrdersQuery.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Link to="/admin/inventory/invoices" className="block no-underline">
          <KpiCard
            label="Invoices Raised"
            value={isLoading ? "—" : recentInvoicesCount.toLocaleString()}
            description="Sales invoices in the last 30 days"
            icon={FileText}
            colorScheme="sky"
            loading={isLoading}
          />
        </Link>
        <Link to="/admin/inventory/bills" className="block no-underline">
          <KpiCard
            label="Bills Recorded"
            value={isLoading ? "—" : recentBillsCount.toLocaleString()}
            description="Vendor bills in the last 30 days"
            icon={Receipt}
            colorScheme="indigo"
            loading={isLoading}
          />
        </Link>
        <Link to="/admin/inventory/invoices" className="block no-underline">
          <KpiCard
            label="Overdue Invoices"
            value={isLoading ? "—" : overdueInvoicesCount.toLocaleString()}
            description="Payments pending from customers"
            icon={AlertTriangle}
            iconClassName={
              overdueInvoicesCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              overdueInvoicesCount > 0
                ? "text-red-600 dark:text-red-400"
                : undefined
            }
            loading={isLoading}
            className={
              overdueInvoicesCount > 0
                ? "bg-linear-to-br from-red-50/60 to-card border-red-200 dark:from-red-950/30 dark:to-card dark:border-red-900/50"
                : undefined
            }
          />
        </Link>
        <Link to="/admin/inventory/bills" className="block no-underline">
          <KpiCard
            label="Overdue Bills"
            value={isLoading ? "—" : overdueBillsCount.toLocaleString()}
            description="Payments due to vendors"
            icon={AlarmClock}
            iconClassName={
              overdueBillsCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              overdueBillsCount > 0 ? "text-red-600 dark:text-red-400" : undefined
            }
            loading={isLoading}
            className={
              overdueBillsCount > 0
                ? "bg-linear-to-br from-red-50/60 to-card border-red-200 dark:from-red-950/30 dark:to-card dark:border-red-900/50"
                : undefined
            }
          />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
        <Suspense
          fallback={<Skeleton className="h-[380px] rounded-xl" />}
        >
          <LineChartCard
            title="Purchase & Sales - Last 30 days"
            description="Daily totals from sales and purchases"
            data={purchaseSalesData}
            xAxisKey="label"
            series={purchaseSalesSeries}
            loading={isLoading}
            emptyText="No invoices or bills in the last 30 days"
            chartHeight={320}
            valueFormatter={formatAmount}
          />
        </Suspense>

        <FinancialSummaryCard
          isLoading={isLoading}
          currencyTotals={currencyTotals}
          recentInvoicesCount={recentInvoicesCount}
          recentBillsCount={recentBillsCount}
          totalInflow={movementReport?.total_inflow ?? 0}
          totalOutflow={movementReport?.total_outflow ?? 0}
          netChange={netChange}
          formatAmount={formatAmount}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Link to="/admin/inventory/sales-orders" className="block no-underline">
          <KpiCard
            label="Sales Orders"
            value={isLoadingOrders ? "—" : salesOrders30dCount.toLocaleString()}
            description="Placed in the last 30 days"
            icon={ShoppingCart}
            colorScheme="sky"
            loading={isLoadingOrders}
          />
        </Link>
        <Link to="/admin/inventory/purchase-orders" className="block no-underline">
          <KpiCard
            label="Purchase Orders"
            value={
              isLoadingOrders ? "—" : purchaseOrders30dCount.toLocaleString()
            }
            description="Raised in the last 30 days"
            icon={ClipboardList}
            colorScheme="indigo"
            loading={isLoadingOrders}
          />
        </Link>
        <Link to="/admin/inventory/sales-orders" className="block no-underline">
          <KpiCard
            label="SO Pending Fulfillment"
            value={isLoadingOrders ? "—" : openSalesOrdersCount.toLocaleString()}
            description="Confirmed, not yet delivered"
            icon={ShoppingCart}
            colorScheme="amber"
            loading={isLoadingOrders}
          />
        </Link>
        <Link to="/admin/inventory/purchase-orders" className="block no-underline">
          <KpiCard
            label="PO Pending Receipt"
            value={
              isLoadingOrders ? "—" : openPurchaseOrdersCount.toLocaleString()
            }
            description="Ordered, not yet received"
            icon={ClipboardList}
            colorScheme="amber"
            loading={isLoadingOrders}
          />
        </Link>
      </div>
    </div>
  );
}

// ─── Tab: Safety, Assets & Rentals ──────────────────────────────────────────────

function SafetyAssetsRentalsSection() {
  // Counted on the server over every item -- the item list caps a page at
  // 100 rows, so counting a fetched page undercounts.
  const safetySummaryQuery = useFetchItemSafetySummary();
  const safetySummary = safetySummaryQuery.data;

  // Each asset/rental query hits the existing list endpoint with a status
  // filter and page_size: 1, reading only the paginated `count`, so this
  // section adds no new backend endpoint.
  const assetsAvailableQuery = useFetchAssetItems({
    status: "AVAILABLE",
    page_size: 1,
  });
  const assetsDeployedQuery = useFetchAssetItems({
    status: "DEPLOYED",
    page_size: 1,
  });
  const assetsMaintenanceQuery = useFetchAssetItems({
    status: "MAINTENANCE",
    page_size: 1,
  });
  const assetsQuarantinedQuery = useFetchAssetItems({
    status: "QUARANTINED",
    page_size: 1,
  });

  const rentalsActiveQuery = useFetchRentalContracts({
    status: "ACTIVE",
    page_size: 1,
  });
  const rentalsOverdueQuery = useFetchRentalContracts({
    status: "OVERDUE",
    page_size: 1,
  });
  const rentalsPartialQuery = useFetchRentalContracts({
    status: "PARTIALLY_RETURNED",
    page_size: 1,
  });

  // AssetSafetyInspectionLog.status is only ever written as PASSED or
  // FAILED (record_asset_safety_inspection decides the outcome the moment
  // it creates the log row) — PENDING is just the field's unused default,
  // never actually assigned, so a `status=PENDING` count would always read
  // 0. "Currently in inspection" instead comes from the asset's own live
  // status, mirroring how AssetItem.status (not any inspection-log status)
  // is what actually flips to IN_INSPECTION and back.
  const assetsInInspectionQuery = useFetchAssetItems({
    status: "IN_INSPECTION",
    page_size: 1,
  });
  // Incidents reported since the start of the current (browser-local)
  // month, counted on the server.
  const incidentsThisMonthQuery = useFetchAssetIncidents({
    reported_at__gte: dayjs().startOf("month").format(),
    page_size: 1,
  });

  const isLoading = safetySummaryQuery.isPending;

  // All six values of Item.safety_status (SAFETY_STATUS_CHOICES on the
  // backend) — SAFE/NOT_SAFE/MONITORING are stored outcomes of a completed
  // formal inspection, OVERDUE/INSPECTION_REQUIRED are derived live from
  // next_due_date vs. today (never stale, never stored — see
  // get_item_safety_status in inspection_service.py), and QUARANTINED is a
  // stored outcome too. All six come from one server-side count
  // (items/safety-summary/), using the same per-item status the list shows.
  const safeItemCount = safetySummary?.SAFE ?? 0;
  const monitoringItemCount = safetySummary?.MONITORING ?? 0;
  const notSafeItemCount = safetySummary?.NOT_SAFE ?? 0;
  const quarantinedItemCount = safetySummary?.QUARANTINED ?? 0;
  const overdueItemCount = safetySummary?.OVERDUE ?? 0;
  const inspectionRequiredItemCount =
    safetySummary?.INSPECTION_REQUIRED ?? 0;

  const assetsAvailableCount = assetsAvailableQuery.data?.count ?? 0;
  const assetsDeployedCount = assetsDeployedQuery.data?.count ?? 0;
  const assetsNeedsAttentionCount =
    (assetsMaintenanceQuery.data?.count ?? 0) +
    (assetsQuarantinedQuery.data?.count ?? 0);

  const rentalsActiveCount = rentalsActiveQuery.data?.count ?? 0;
  const rentalsOverdueCount = rentalsOverdueQuery.data?.count ?? 0;
  const rentalsPartialCount = rentalsPartialQuery.data?.count ?? 0;

  const assetsInInspectionCount = assetsInInspectionQuery.data?.count ?? 0;
  const incidentsThisMonthCount = incidentsThisMonthQuery.data?.count ?? 0;

  const isLoadingAssets =
    assetsAvailableQuery.isPending ||
    assetsDeployedQuery.isPending ||
    assetsMaintenanceQuery.isPending ||
    assetsQuarantinedQuery.isPending ||
    rentalsActiveQuery.isPending ||
    rentalsOverdueQuery.isPending ||
    rentalsPartialQuery.isPending ||
    assetsInInspectionQuery.isPending ||
    incidentsThisMonthQuery.isPending;

  return (
    <div className="flex flex-col gap-4">
      <SafetyMetricsCard
        isLoading={isLoading}
        safeItemCount={safeItemCount}
        notSafeItemCount={notSafeItemCount}
        monitoringItemCount={monitoringItemCount}
        quarantinedItemCount={quarantinedItemCount}
        overdueItemCount={overdueItemCount}
        inspectionRequiredItemCount={inspectionRequiredItemCount}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Link to="/admin/inventory/asset-items" className="block no-underline">
          <KpiCard
            label="Available Assets"
            value={isLoadingAssets ? "—" : assetsAvailableCount.toLocaleString()}
            description="Ready to deploy or rent out"
            icon={Wrench}
            colorScheme="emerald"
            loading={isLoadingAssets}
          />
        </Link>
        <Link to="/admin/inventory/asset-items" className="block no-underline">
          <KpiCard
            label="Deployed / On Rent"
            value={isLoadingAssets ? "—" : assetsDeployedCount.toLocaleString()}
            description="Currently out with a customer or task"
            icon={Truck}
            colorScheme="sky"
            loading={isLoadingAssets}
          />
        </Link>
        <Link to="/admin/inventory/asset-items" className="block no-underline">
          <KpiCard
            label="Assets Needing Attention"
            value={
              isLoadingAssets ? "—" : assetsNeedsAttentionCount.toLocaleString()
            }
            description="In maintenance or quarantined"
            icon={AlertTriangle}
            iconClassName={
              assetsNeedsAttentionCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              assetsNeedsAttentionCount > 0
                ? "text-red-600 dark:text-red-400"
                : undefined
            }
            loading={isLoadingAssets}
            className={
              assetsNeedsAttentionCount > 0
                ? "bg-linear-to-br from-red-50/60 to-card dark:from-red-950/30 dark:to-card"
                : undefined
            }
          />
        </Link>
        <Link to="/admin/inventory/rental-contracts" className="block no-underline">
          <KpiCard
            label="Overdue Returns"
            value={isLoadingAssets ? "—" : rentalsOverdueCount.toLocaleString()}
            description="Rental contracts past expected return"
            icon={AlarmClock}
            iconClassName={
              rentalsOverdueCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              rentalsOverdueCount > 0
                ? "text-red-600 dark:text-red-400"
                : undefined
            }
            loading={isLoadingAssets}
            className={
              rentalsOverdueCount > 0
                ? "bg-linear-to-br from-red-50/60 to-card dark:from-red-950/30 dark:to-card"
                : undefined
            }
          />
        </Link>
        <Link to="/admin/inventory/rental-contracts" className="block no-underline">
          <KpiCard
            label="Active Rentals"
            value={isLoadingAssets ? "—" : rentalsActiveCount.toLocaleString()}
            description="Contracts currently checked out"
            icon={Truck}
            colorScheme="indigo"
            loading={isLoadingAssets}
          />
        </Link>
        <Link to="/admin/inventory/rental-contracts" className="block no-underline">
          <KpiCard
            label="Partially Returned"
            value={isLoadingAssets ? "—" : rentalsPartialCount.toLocaleString()}
            description="Rentals with an open balance"
            icon={Undo2}
            colorScheme="amber"
            loading={isLoadingAssets}
          />
        </Link>
        <Link to="/admin/inventory/inspections" className="block no-underline">
          <KpiCard
            label="Assets In Inspection"
            value={
              isLoadingAssets ? "—" : assetsInInspectionCount.toLocaleString()
            }
            description="Currently with an inspector"
            icon={ClipboardCheck}
            colorScheme="violet"
            loading={isLoadingAssets}
          />
        </Link>
        <Link to="/admin/inventory/asset-incidents" className="block no-underline">
          <KpiCard
            label="Incidents (this month)"
            value={
              isLoadingAssets ? "—" : incidentsThisMonthCount.toLocaleString()
            }
            description="Asset incidents reported"
            icon={Siren}
            iconClassName={
              incidentsThisMonthCount > 0
                ? "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400"
                : "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
            }
            valueClassName={
              incidentsThisMonthCount > 0
                ? "text-red-600 dark:text-red-400"
                : undefined
            }
            loading={isLoadingAssets}
          />
        </Link>
      </div>
    </div>
  );
}

// ─── Page shell ─────────────────────────────────────────────────────────────────
// Each tab below is its own component so its hooks (and the API calls they
// trigger) only run once Radix mounts that tab's TabsContent — i.e. only
// after the user actually clicks the tab, AND only once its permission gate
// (below) lets its TabsTrigger/TabsContent render at all.
//
// Every widget on a tab mixes data from more than one backend resource
// (e.g. Overview's KPIs blend ReportViewSet's stock summary with
// ItemViewSet's low-stock/reorder counts), so a tab is all-or-nothing: if
// the caller holds only some of what it needs, showing it half-populated
// (real numbers next to permanently-blank ones) would misread as "there is
// no data" rather than "you can't see this" — same failure this whole audit
// exists to close. Hide the whole tab instead, mirroring the Admin
// Dashboard's own "hide the widget, don't show an error" rule.
function InventoryDashboardPage() {
  const { data: capabilities, isPending: permissionsPending } =
    useMyPermissions();
  const canStockView = can(capabilities, "inventory.stock.view");
  const canReportView = can(capabilities, "inventory.report.view");
  const canSalesManage = can(capabilities, "inventory.sales.manage");
  const canPurchasingManage = can(capabilities, "inventory.purchasing.manage");

  // Overview: ReportViewSet.stock-summary/movement-report (report.view) +
  // ItemViewSet.low_stock/reorder_needed + StockTransactionViewSet list
  // (both stock.view).
  const canViewOverview = canStockView && canReportView;
  // Purchases & Sales: ReportViewSet.movement-report (report.view) +
  // InvoiceViewSet/SalesOrderViewSet (sales.manage) +
  // BillViewSet/PurchaseOrderViewSet (purchasing.manage).
  const canViewPurchasesSales =
    canReportView && canSalesManage && canPurchasingManage;
  // Safety, Assets & Rentals: ItemViewSet.safety_summary + AssetItemViewSet/
  // RentalLeaseContractViewSet/AssetIncidentLogViewSet list (all stock.view).
  const canViewSafetyAssets = canStockView;

  const availableTabs = [
    canViewOverview && "overview",
    canViewPurchasesSales && "purchases-sales",
    canViewSafetyAssets && "safety-assets-rentals",
  ].filter((v): v is string => Boolean(v));

  const [tab, setTab] = useState(availableTabs[0] ?? "overview");
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(tab)) {
      setTab(availableTabs[0]);
    }
    // availableTabs is derived fresh every render — compare by content, not
    // reference, so this only fires when the visible set actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTabs.join(","), tab]);

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="flex flex-col gap-6 p-6 pt-0">
        {(() => {
          const header = (
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Inventory Dashboard
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Live overview of stock health, purchases, sales, and recent
                activity
              </p>
            </div>
          );

          if (permissionsPending) {
            return (
              <>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  {header}
                </div>
                <Skeleton className="h-64 rounded-lg" />
              </>
            );
          }

          if (availableTabs.length === 0) {
            return (
              <>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  {header}
                </div>
                <Card>
                  <CardContent className="py-10 text-center text-sm text-muted-foreground">
                    You don't have permission to view any inventory dashboard
                    data yet.
                  </CardContent>
                </Card>
              </>
            );
          }

          return (
            <Tabs value={tab} onValueChange={setTab} className="gap-6">
              {/* On desktop the tab strip sits beside the title, not on its
                  own row below it; mobile keeps the stacked layout. */}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                {header}
                <TabsList className="w-full justify-start lg:w-auto lg:shrink-0">
                  {canViewOverview && (
                    <TabsTrigger value="overview" className="gap-1.5">
                      <LayoutDashboard className="lg:hidden" />
                      <span>Overview</span>
                    </TabsTrigger>
                  )}
                  {canViewPurchasesSales && (
                    <TabsTrigger value="purchases-sales" className="gap-1.5">
                      <ShoppingCart className="lg:hidden" />
                      <span className="lg:hidden">Sales</span>
                      <span className="hidden lg:inline">Purchases & Sales</span>
                    </TabsTrigger>
                  )}
                  {canViewSafetyAssets && (
                    <TabsTrigger value="safety-assets-rentals" className="gap-1.5">
                      <ShieldCheck className="lg:hidden" />
                      <span className="lg:hidden">Assets</span>
                      <span className="hidden lg:inline">
                        Safety, Assets & Rentals
                      </span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {canViewOverview && (
                <TabsContent value="overview">
                  <OverviewSection />
                </TabsContent>
              )}
              {canViewPurchasesSales && (
                <TabsContent value="purchases-sales">
                  <PurchasesSalesSection />
                </TabsContent>
              )}
              {canViewSafetyAssets && (
                <TabsContent value="safety-assets-rentals">
                  <SafetyAssetsRentalsSection />
                </TabsContent>
              )}
            </Tabs>
          );
        })()}
      </div>
    </SidebarInset>
  );
}
